import React from "react"
import { RiskReportData } from "hooks/useRiskReport"
import styled from "styled-components"
import { Paragraph } from "components/_blueprint/Typography"
import { desktop, mobile, tablet } from "components/_blueprint/Layout"

export const JointCalculations = ({ riskReport }: { riskReport: RiskReportData }) => {
  // Assert the first applicant's data. We that at least
  if (riskReport.riskIndividual.length < 1)
    throw new Error(`Individual risk report data unavailable`)
  const leadApplicant = riskReport.riskIndividual[0]

  // If it's a joint applicant report, use the combined calculations
  // If it's a single applicant report, use the individual calculations
  const combinedRisk = riskReport.riskJoint
    ? riskReport.riskJoint.combinedRisk
    : leadApplicant.combinedRisk
  const deathRisk = riskReport.riskJoint ? riskReport.riskJoint.deathRisk : leadApplicant.deathRisk
  const illnessRisk = riskReport.riskJoint
    ? riskReport.riskJoint.illnessRisk
    : leadApplicant.illnessRisk
  const incomeRisk = riskReport.riskJoint
    ? riskReport.riskJoint.incomeRisk
    : leadApplicant.incomeRisk

  return (
    <RoundedBorder>
      <DataSection>
        {/* Combined Risk */}

        <CombinedRiskSection>
          <CombinedRiskVal>
            {Math.floor(combinedRisk)}
            <Percent>%</Percent>
          </CombinedRiskVal>
          <CombinedRiskText>
            chance of death, serious illness or being unable to work before reaching retirement age
          </CombinedRiskText>
        </CombinedRiskSection>

        {/* Breakdown Risk 1. Death */}

        <BreakdownRiskSection>
          <BreakdownRiskRow>
            <BreakdownRiskBox>
              <BreakdownRiskVal>{Math.floor(deathRisk)}%</BreakdownRiskVal>
              <BreakdownRiskText>Chance</BreakdownRiskText>
            </BreakdownRiskBox>
            <BreakdownRiskDescSm>
              <BreakdownRiskDescContext>
                <Bold>Death</Bold> - You die before reaching retirement
              </BreakdownRiskDescContext>
              <BreakdownRiskDescPayout>
                <Bold>Life insurance</Bold> - could pay a lump sum in the event of death
              </BreakdownRiskDescPayout>
            </BreakdownRiskDescSm>
            <BreakdownRiskDescLg>
              <SmallRiskDescLabel>Death</SmallRiskDescLabel>
              <BreakdownRiskDescContext>
                You die before reaching retirement
              </BreakdownRiskDescContext>
              <BreakdownRiskDescPayout>
                <Bold>Life insurance</Bold> could pay a lump sum in the event of death
              </BreakdownRiskDescPayout>
            </BreakdownRiskDescLg>
          </BreakdownRiskRow>

          {/* Breakdown Risk 2. Serious Illness */}

          <BreakdownRiskRow>
            <BreakdownRiskBox>
              <BreakdownRiskVal>{Math.floor(illnessRisk)}%</BreakdownRiskVal>
              <BreakdownRiskText>Chance</BreakdownRiskText>
            </BreakdownRiskBox>
            <BreakdownRiskDescSm>
              <BreakdownRiskDescContext>
                <Bold>Serious Illness</Bold> - A serious illness stops you from working before
                retirement
              </BreakdownRiskDescContext>
              <BreakdownRiskDescPayout>
                <Bold>Critical illness cover</Bold> - could pay a lump sum in the event of a serious
                illness
              </BreakdownRiskDescPayout>
            </BreakdownRiskDescSm>
            <BreakdownRiskDescLg>
              <SmallRiskDescLabel>Serious Illness</SmallRiskDescLabel>
              <BreakdownRiskDescContext>
                A serious illness stops you from working before reaching retirement
              </BreakdownRiskDescContext>
              <BreakdownRiskDescPayout>
                <Bold>Critical illness cover</Bold> could pay a lump sum in the event of serious
                illness diagnosis
              </BreakdownRiskDescPayout>
            </BreakdownRiskDescLg>
          </BreakdownRiskRow>

          {/* Breakdown Risk 3. Unable to Work */}

          <BreakdownRiskRow>
            <BreakdownRiskBox>
              <BreakdownRiskVal>{Math.floor(incomeRisk)}%</BreakdownRiskVal>
              <BreakdownRiskText>Chance</BreakdownRiskText>
            </BreakdownRiskBox>
            <BreakdownRiskDescSm>
              <BreakdownRiskDescContext>
                <Bold>Unable to work</Bold> - Illness or injury stops you working for 4 weeks or
                more before retirement
              </BreakdownRiskDescContext>
              <BreakdownRiskDescPayout>
                <Bold>Income protection</Bold> - pays you a regular amount if you're unable to work
              </BreakdownRiskDescPayout>
            </BreakdownRiskDescSm>
            <BreakdownRiskDescLg>
              <SmallRiskDescLabel>Unable to work</SmallRiskDescLabel>
              <BreakdownRiskDescContext>
                Illness or injury stops you working for 4 weeks or more before reaching retirement
              </BreakdownRiskDescContext>
              <BreakdownRiskDescPayout>
                <Bold>Income protection</Bold> could pay you a regular amount if unable to work
              </BreakdownRiskDescPayout>
            </BreakdownRiskDescLg>
          </BreakdownRiskRow>
        </BreakdownRiskSection>
      </DataSection>

      {/* Explainer Copy */}

      <ExplainerSection>
        <ExplainerHeader>How your results are calculated</ExplainerHeader>
        <Paragraph weight={400}>
          We review insurer claim rates to assess the risk of being unable to work and UK population
          statistics for serious illness and death rates, in combination with personal details about
          applicants such as age, intended retirement age, sex and smoker status to estimate the
          likelihood of a serious life event occurring between an applicant's current age and
          anticipated retirement age. It is a statistical guide based on the personal details
          provided, and where we don t hold every detail about you, we calculate averages to fill in
          the gaps. The longer the time from current age to retirement, the higher the statistical
          risk. Discuss the risks with a Mojo Protection Expert to understand how they apply to you.
        </Paragraph>
        <ExplainerHeader>Data sources</ExplainerHeader>
        <Paragraph weight={400}>
          <Bold>Inability to Work</Bold> is based on on Income Protection claim rates (Institute of
          Actuaries IP06' series) and is indicative only for ages above 70 (adjusted for smoker
          status). <Bold>Serious Illness</Bold> is based on critical illness and death rates
          (Institute of Actuaries AC16' series). <Bold>Death</Bold> is based on Based on mortality
          tables (Institute of Actuaries AM16' series).
        </Paragraph>
      </ExplainerSection>
    </RoundedBorder>
  )
}

const RoundedBorder = styled.div({
  border: "1px solid #D1DDFC",
  borderRadius: "25px",
  marginBottom: "32px",
})

const DataSection = styled.div({
  maxWidth: 1400,

  display: "flex",

  [mobile]: {
    flexDirection: "column",
    padding: "16px",
    gap: "12px",
  },
  [tablet]: {
    flexDirection: "column",
    padding: "29px",
    gap: "12px",
  },
  [desktop]: {
    flexDirection: "row",
    padding: "58px",
    gap: "24px",
  },
})

const CombinedRiskSection = styled.div({
  background: "#D1DDFC",
  borderRadius: "16px",

  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  textAlign: "center",

  [mobile]: {
    padding: "24px",
  },
  [tablet]: {
    width: "100%",
    height: "180px",
    padding: "24px",
  },
  [desktop]: {
    width: "383px",
    height: "383px",
    margin: "0",
    padding: "48px",
  },
})

const CombinedRiskVal = styled.p({
  fontWeight: 700,
  margin: 0,

  [mobile]: {
    fontSize: "80px",
  },
  [tablet]: {
    fontSize: "80px",
  },
  [desktop]: {
    fontSize: "160px",
  },
})

const CombinedRiskText = styled.p({
  maxWidth: "372px",
  margin: "0 auto",
})

const Percent = styled.span({
  [mobile]: {
    fontSize: "40px",
  },
  [tablet]: {
    fontSize: "40px",
  },
  [desktop]: {
    fontSize: "80px",
  },
})

const BreakdownRiskSection = styled.div({
  flex: 1,

  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",

  [mobile]: {
    gap: "12px",
  },
  [tablet]: {
    gap: "12px",
  },
  [desktop]: {
    gap: "24px",
  },
})

const BreakdownRiskRow = styled.div({
  display: "flex",

  [mobile]: {
    gap: "12px",
  },
  [tablet]: {
    gap: "12px",
  },
  [desktop]: {
    gap: "24px",
  },
})

const BreakdownRiskBox = styled.div({
  background: "#D1DDFC",
  borderRadius: "16px",

  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  textAlign: "center",

  width: 100,
  ["@media (max-width: 458px)"]: {
    height: "atuo",
  },
  ["@media (min-width: 459px)"]: {
    height: 100,
  },
})

const BreakdownRiskVal = styled.div({
  fontSize: "1.5rem",
  fontWeight: 700,
  margin: 0,
})

const BreakdownRiskText = styled.div({
  margin: 0,
})

/*
 * Why are we using custom breakpoint here?
 *
 * Dealing with text wrapping in the breakdown section is very tricky. We cam't
 * use the normal `mobile`, `desktop`, etc. breakpoints without the UI looking
 * awful. We need more granular control.
 *
 * Why do we use `BreakdownRiskDescSm` and `BreakdownRiskDescLg` ?
 *
 * Using a dedicated title for each breakdown doesn't work on specific widths.
 * You end up with squashed text, and a lot of unused space. To make things
 * look pretty, I've inlined the breakdown title on some of these dimensions
 */
const sm = "@media (max-width: 767px)"
const md = "@media (min-width: 768px) and (max-width: 1023px)"
const lg = "@media (min-width: 1024px) and (max-width: 1259px)"
const xl = "@media (min-width: 1260px)"

const BreakdownRiskDescSm = styled.div({
  [sm]: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-evenly",
    flex: 1,
    gap: "8px",
  },
  [md]: {
    display: "none",
  },
  [lg]: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    flex: 1,
  },
  [xl]: {
    display: "none",
  },
})

const BreakdownRiskDescLg = styled.div({
  [sm]: {
    display: "none",
  },
  [md]: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    flex: 1,
  },
  [lg]: {
    display: "none",
  },
  [xl]: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    flex: 1,
  },
})

const SmallRiskDescLabel = styled.p({
  fontSize: "1.125rem",
  fontWeight: 600,
  margin: 0,
  padding: "0 12px",
})

const BreakdownRiskDescContext = styled.p({
  fontSize: "0.875rem",
  color: "#3B4258",
  margin: 0,
  padding: "0 12px",
})

const BreakdownRiskDescPayout = styled.p({
  fontSize: "0.875rem",
  background: "#FFEBDC",
  borderRadius: "12px",
  padding: "12px",
  margin: 0,

  width: "fit-content",
})

const Bold = styled.span({
  color: "black",
  fontWeight: 600,
})

const ExplainerSection = styled.div({
  background: "#F8F9FC",
  borderRadius: "0 0 25px 25px",

  [mobile]: {
    padding: "24px",
  },
  [tablet]: {
    padding: "36px",
  },
  [desktop]: {
    padding: "48px",
  },
})

const ExplainerHeader = styled.p({
  margin: "0 0 16px",

  fontSize: "1.5rem",
  fontWeight: 600,
})
